export interface IntegrationDto {
    id: number;
    name: string;
    supplierType: number;
    supplierName: string;
    lastPullDate: string | null;
    lastFullSyncDate: string | null;
    lastFilterUpdateDate: string | null;
    lastCategoriesUpdateDate: string | null;
    data: string;
    jobConfiguration: string | null;
    calculatedExportPath: string;
    averageExecutionTimeSeconds: number;
    // Дополнительные поля для отображения
    productCount?: number;
    jobCount?: number;
    isActive?: boolean;
    lastPullRun?: PullRunDto | null;
    // Настройки доставки
    stockLifeTimeHours?: number;
    deliveryTimeHours?: number;
    saveExpectedToMainProduct?: boolean;
    isStockQuantitySource?: boolean;
    shippingDaysList?: IntegrationShippingDayDto[];
}

export interface IntegrationShippingDayDto {
    id: number;
    integrationId: number;
    dayOfWeek: number;
    cutoffTime: string;
}

export interface Job {
    integrationId: number;
    jobNumber: number;
    jobId: string;
    cron: string;
    timeZoneId: string | null;
    nextExecution: string | null;
    lastExecution: string | null;
    lastJobId: string | null;
    queue: string;
    error: string | null;
    createdAt: string;
    removed: boolean;
}

export interface PullRunDto {
    id: number;
    integrationId: number;
    startTime: string;
    endTime: string | null;
    duration: number | null; // in seconds
    success: boolean;
    errorMessage: string | null;
    output: string | null;
    errorOutput: string | null;
}

export interface JobConfiguration {
    loadFiltersBeforePull?: boolean;
    filtersFilePath?: string;
    exportType?: 'none' | 'file';
    exportFilePath?: string;
    backupEnabled?: boolean;
    backupFormat?: string;
}

export interface CreateIntegrationRequest {
    name: string;
    supplierName: string;
    data: string;
    stockLifeTimeHours?: number;
    deliveryTimeHours?: number;
    saveExpectedToMainProduct?: boolean;
    isStockQuantitySource?: boolean;
}

export interface UpdateIntegrationRequest {
    name?: string;
    supplierName?: string;
    data?: string;
    isActive?: boolean;
    stockLifeTimeHours?: number;
    deliveryTimeHours?: number;
    saveExpectedToMainProduct?: boolean;
    isStockQuantitySource?: boolean;
    shippingDays?: ShippingDayFormData[];
}

export interface ShippingDayFormData {
    id?: number;
    dayOfWeek: number;
    cutoffTime: string;
}

export interface IntegrationDetailsDto {
    id: number;
    name: string;
    supplierName: string;
    supplierType: number;
    data: string;
    jobConfiguration: string | null;
    averageExecutionTimeSeconds: number;
    lastPullDate: string | null;
    lastFullSyncDate: string | null;
    lastFilterUpdateDate: string | null;
    lastCategoriesUpdateDate: string | null;
    createdAt: string;
    updatedAt: string;
    jobs: Job[];
    isActive: boolean;
}

export interface Supplier {
    supplierType: number;
    name: string;
    data?: string;
}

export type IntegrationTab = 'info' | 'settings' | 'jobs';

// Admin panel types
export interface UserDto {
    id: string;
    userName: string;
    email: string;
    roles: string[];
    createdAt: string | null;
    lastLogin: string | null;
    isActive: boolean;
}

export interface ShopStaffUserDto {
    id: string;
    userName: string;
    email: string | null;
}

export interface UserDetailsDto {
    id: string;
    userName: string;
    email: string;
    emailConfirmed: boolean;
    roles: string[];
    claims: string[];
    createdAt: string | null;
    lastLogin: string | null;
    lockoutEnd: string | null;
    isActive: boolean;
}

export interface CreateUserRequest {
    userName: string;
    email: string;
    password: string;
    roles: string[];
}

export interface UpdateUserRequest {
    userName?: string;
    email?: string;
    password?: string;
    roles: string[];
}

export interface B2BClientDto {
    id: number;
    name: string;
    description: string | null;
    isActive: boolean;
    apiKeyPrefix: string;
    apiKeySuffix: string;
    createdAt: string;
    updatedAt: string | null;
    lastUsedAt: string | null;
}

export interface B2BClientWithApiKeyDto {
    client: B2BClientDto;
    apiKey: string;
}

export interface CreateB2BClientRequest {
    name: string;
    description?: string;
}

export interface UpdateB2BClientRequest {
    name: string;
    description?: string;
}

export interface ShopCompanyDto {
    id: number;
    name: string;
    description: string | null;
    defaultManagerUserId: number;
    defaultManagerUserName: string;
    isActive: boolean;
    showPrice1: boolean;
    showPrice2: boolean;
}

export interface ShopBuyerDto {
    userId: number;
    userName: string;
    email: string;
    companyId: number;
    managerUserId: number | null;
    managerUserName: string | null;
    showPrice1: boolean | null;
    showPrice2: boolean | null;
}

export interface ShopCompanyDetailsDto extends ShopCompanyDto {
    buyers: ShopBuyerDto[];
}

export interface CreateShopCompanyRequest {
    name: string;
    description?: string;
    defaultManagerUserId: number;
    showPrice1: boolean;
    showPrice2: boolean;
}

export interface UpdateShopCompanyRequest {
    name: string;
    description?: string;
    defaultManagerUserId: number;
    isActive: boolean;
    showPrice1: boolean;
    showPrice2: boolean;
}

export interface CreateShopBuyerRequest {
    userName: string;
    email: string;
    password: string;
    managerUserId?: number | null;
    showPrice1?: boolean | null;
    showPrice2?: boolean | null;
}

export interface UpdateShopBuyerRequest {
    userName?: string;
    email?: string;
    managerUserId?: number | null;
    showPrice1?: boolean | null;
    showPrice2?: boolean | null;
}

export interface SetBuyerPasswordRequest {
    password: string;
}

export const ShopOrderStatus = {
    Draft: 0,
    Sent: 1,
    Closed: 2,
    Cancelled: 3
} as const;

export type ShopOrderStatusValue = typeof ShopOrderStatus[keyof typeof ShopOrderStatus];

export interface ShopOrderLineDto {
    id: number;
    mainProductId: number;
    name: string;
    sku: string | null;
    unitPrice: number;
    quantity: number;
    lineComment: string | null;
}

export interface ShopOrderDto {
    id: number;
    number?: string;
    companyId: number;
    companyName: string;
    authorUserId: number;
    authorUserName: string;
    managerUserId: number | null;
    managerUserName: string | null;
    status: ShopOrderStatusValue;
    comment: string | null;
    createdAt: string;
    updatedAt: string | null;
    sentAt: string | null;
    closedAt: string | null;
    cancelledAt: string | null;
    lines: ShopOrderLineDto[];
}

export interface ShopOrderCommentRequest {
    comment: string | null;
}

export interface CancelShopOrderRequest {
    comment: string;
}

// Category types
export interface CategoryDto {
    id: number;
    name: string;
    description?: string;
    parentId?: number;
    parentName?: string;
    productCount: number;
    createdAt: string;
}

export interface CreateCategoryRequest {
    name: string;
    description?: string;
    parentId?: number;
}

export interface UpdateCategoryRequest {
    name?: string;
    description?: string;
    parentId?: number;
}

// Product types
export interface ProductDto {
    id: number;
    name: string;
    description?: string;
    categoryId: number;
    categoryName: string;
    sku?: string;
    price?: number;
    integrationCount: number;
    createdAt: string;
}

export interface CreateProductRequest {
    name: string;
    description?: string;
    categoryId: number;
    sku?: string;
    price?: number;
}

export interface UpdateProductRequest {
    name?: string;
    description?: string;
    categoryId?: number;
    sku?: string;
    price?: number;
}

export interface SupplierDto {
    supplierType: number;
    name: string;
    data?: string;
}

export interface UpdateSupplierRequest {
    useWarehouses: boolean;
    warehouses?: string[];
}

export type NavigationSection = 'integrations' | 'users' | 'clients' | 'suppliers' | 'diagnostics' | 'categories' | 'products' | 'attributes' | 'b2b-clients' | 'shop-companies' | 'shop-orders' | 'maintenance' | 'operations' | 'smtp' | 'order-lifetime';

export interface NavigationChildItem {
    key: NavigationSection;
    label: string;
}

export interface NavigationItem {
    key?: NavigationSection;
    label: string;
    icon?: string;
    children?: NavigationChildItem[];
}

export interface SmtpSettingsDto {
    host: string;
    port: number;
    security: string;
    username: string;
    hasPassword: boolean;
    fromEmail: string;
    fromName: string;
}

export interface SaveSmtpSettingsRequest {
    host: string;
    port: number;
    security: string;
    username: string;
    password?: string;
    fromEmail: string;
    fromName: string;
}

export interface OrderLifetimeSettingsDto {
    draftLifetimeDays: number;
    sentLifetimeDays: number;
    completedLifetimeDays: number;
    cancelledLifetimeDays: number;
    archiveRetentionDays: number;
}

export interface SaveOrderLifetimeSettingsRequest {
    draftLifetimeDays: number;
    sentLifetimeDays: number;
    completedLifetimeDays: number;
    cancelledLifetimeDays: number;
    archiveRetentionDays: number;
}

export interface IntegrationDiagnosticsDto {
    integrationId: number;
    integrationName: string;
    supplierName: string;
    loadedProductsCount: number;
    withoutCategoryCount: number;
    unlinkedToMainProductsCount: number;
    expiredProductsCount: number;
    withoutPriceCount: number;
    withoutVendorCount: number;
}

export interface UnlinkedToMainProductsCategoryRowDto {
    supplierCategoryId?: number;
    supplierCategoryName: string;
    count: number;
}

export interface UnlinkedToMainProductsByCategoryDto {
    integrationId: number;
    integrationName: string;
    supplierName: string;
    rows: UnlinkedToMainProductsCategoryRowDto[];
}

export interface UnlinkedToMainProductsProductDto {
    id: number;
    externalId?: string;
    name: string;
    sku?: string;
}

export interface MaintenanceOperationDto {
    code: string;
    title: string;
    description: string;
    riskLevel: string;
}

export interface MaintenanceRunDto {
    id: number;
    operationCode: string;
    operationTitle: string;
    status: string;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    requestedByUserName: string | null;
    hangfireJobId: string | null;
    summaryJson: string | null;
    errorMessage: string | null;
}

export interface MaintenanceRunLogDto {
    id: number;
    createdAt: string;
    level: string;
    message: string;
    detailsJson: string | null;
}

export interface MaintenanceRunDetailsDto extends MaintenanceRunDto {
    reportJson: string | null;
    logs: MaintenanceRunLogDto[];
}

// Categories selection types
export interface CategorySelection {
    integrationId: number;
    categoryExternalId: string;
    isSelected: boolean;
}

export interface SaveCategorySelectionsRequest {
    integrationId: number;
    selections: CategorySelection[];
}

// Categories tree types
export interface IntegrationCategoriesDto {
    integrationId: number;
    name: string;
    categories: CategoryNodeDto[];
    productCount: number | undefined;
}

export interface CategoryNodeDto {
    id: number;
    parentId: number | undefined;
    externalId: string;
    name: string;
    children: CategoryNodeDto[];
    productCount: number | undefined;
    mappedClientCategoryIds?: string;
    mappedClientCategories?: MappedClientCategoryDto[];
}

export interface MappedClientCategoryDto {
    id: number;
    name: string;
}

// Auth types
export interface User {
    id: string;
    username: string;
    email: string;
    roles: string[];
    isShopManager?: boolean;
}

// Operations types
export interface OperationDefinitionDto {
    code: string;
    title: string;
    description: string;
    domain: string;
    riskLevel: string;
    supportsManualRun: boolean;
    supportsSchedule: boolean;
    requiresConfirmation: boolean;
    requiredCapability: string;
    customUiKey: string | null;
    parameters: OperationParameterDto[];
}

export interface OperationParameterDto {
    name: string;
    label: string;
    type: string;
    required: boolean;
    defaultValue: string | null;
    options: OperationParameterOptionDto[];
}

export interface OperationParameterOptionDto {
    value: string;
    label: string;
}

export interface OperationScheduleDto {
    enabled: boolean;
    cron: string | null;
    timeZoneId: string | null;
    nextExecution: string | null;
    lastExecution: string | null;
    lastJobId: string | null;
    error: string | null;
    parametersJson: string;
}

export interface OperationScheduleSaveRequest {
    cron: string;
    timeZoneId: string;
    parametersJson: string;
}

export interface OperationRunDto {
    id: number;
    operationCode: string;
    operationTitle: string;
    status: string;
    trigger: string;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    requestedByUserName: string | null;
    hangfireJobId: string | null;
    parametersJson: string | null;
    summaryJson: string | null;
    errorMessage: string | null;
}

export interface OperationRunDetailsDto extends OperationRunDto {
    reportJson: string | null;
    logs: OperationRunLogDto[];
}

export interface OperationRunLogDto {
    id: number;
    createdAt: string;
    level: string;
    message: string;
    detailsJson: string | null;
}